
import React, { useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  useTheme,
  Container,
  Divider,
  Stack,
  Chip,
} from "@mui/material";

import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";

const faqSections = [
  {
    title: "Getting Started",
    questions: [
      {
        question: "What is ArthGyan?",
        answer:
          "ArthGyan is a community focused and community first financial platform that helps government employees make better financial decisions—whether it’s investment, banking products, insurance, or financial planning.",
      },
      {
        question: "Who can use ArthGyan?",
        answer:
          "Government employees (Central & State), PSU employees, and anyone looking for simple financial guidance (basic access).",
      },
      {
        question: "Is ArthGyan free to use?",
        answer: "Yes, ArthGyan is completely free for users.",
      },
      {
        question: "How do I get started?",
        answer:
          "Just sign up on the app, explore content, and choose the services you need.",
      },
    ],
  },
  {
    title: "Services on ArthGyan",
    questions: [
      {
        question: "What can I do on ArthGyan?",
        answer:
          "You can buy investment products like mutual funds and invest in FDs, explore insurance plans, apply for loans, and get guidance on financial decisions.",
      },
      {
        question: "Does ArthGyan provide financial advice?",
        answer:
          "ArthGyan provides easy-to-understand guidance to help you make informed decisions.",
      },
    ],
  },
  {
    title: "Insurance",
    questions: [
      {
        question: "What types of insurance can I get?",
        answer:
          "Life Insurance (Term Plans), Health Insurance, and Motor Insurance.",
      },
      {
        question:
          "Why do I need insurance if I already have government benefits?",
        answer:
          "Government benefits may not be enough for rising medical costs and family protection. Insurance gives extra financial security.",
      },
      {
        question: "How does ArthGyan help me choose the right policy?",
        answer:
          "It explains policies in simple terms, helps compare options, and recommends what suits your needs.",
      },
    ],
  },
  {
    title: "Loans",
    questions: [
      {
        question: "What types of loans are available?",
        answer: "Personal Loan, Home Loan, and Car Loan.",
      },
      {
        question: "Will I get better loan offers as a government employee?",
        answer:
          "Yes, government employees often get lower interest rates and higher approval chances.",
      },
      {
        question: "Does ArthGyan charge anything for loans?",
        answer: "No, you don’t pay anything to use ArthGyan.",
      },
    ],
  },
  {
    title: "Trust & Safety",
    questions: [
      {
        question: "Is ArthGyan safe to use?",
        answer: "Yes, your data is secure and protected.",
      },
      {
        question: "Will my information be shared?",
        answer:
          "Your data is only shared with partners when needed to provide a service.",
      },
      {
        question: "Will I get spam calls?",
        answer:
          "No. ArthGyan ensures limited and relevant communication only.",
      },
    ],
  },
  {
    title: "Using the App",
    questions: [
      {
        question: "Can I compare different options?",
        answer:
          "Yes, you can explore and compare products before making a decision.",
      },
      {
        question: "Can I use ArthGyan anytime?",
        answer: "Yes, the app is available 24/7.",
      },
    ],
  },
  {
    title: "Support",
    questions: [
      {
        question: "What if I need help?",
        answer:
          "You can contact support through in-app help, email, WhatsApp, or call (8AM–8PM).",
      },
      {
        question: "Will someone guide me personally?",
        answer:
          "Yes, support is available to help you understand and choose better.",
      },
    ],
  },
  {
    title: "Common Questions",
    questions: [
      {
        question: "Are there any hidden charges?",
        answer: "No, there are no hidden costs.",
      },
      {
        question: "Do I have to buy anything?",
        answer: "No, you can simply learn and explore without buying.",
      },
      {
        question: "Why should I use ArthGyan?",
        answer:
          "Because it helps you avoid wrong financial decisions, understand money easily, and choose the right products with confidence.",
      },
    ],
  },
];

const FAQPage = () => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const [expandedIndex, setExpandedIndex] = useState(null);

  const totalQuestions = faqSections.reduce(
    (total, section) => total + section.questions.length,
    0
  );

  const handleAccordionChange = (id) => (_, isExpanded) => {
    setExpandedIndex(isExpanded ? id : null);
  };

  return (
    <>
      <Box
        sx={{
          pt: { xs: 10, md: 14 },
          pb: { xs: 6, md: 2 },
          px: 2,

        }}
      >

        {/* Decorative background */}


        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          {/* Heading */}


          <Typography
            component="h1"
            sx={{
              fontSize: { xs: "20px", sm: "28px", md: "40px" },
              fontWeight: 600,
              textAlign: "center",
              lineHeight: 1.1,
              mb: 3,
              animation: "fadeUp 0.9s ease-out both",
              color: theme.palette.text.primary,
              position: "relative",
              display: "inline-block",
              width: "100%",
            }}
          >
            Frequently Asked{" "}
            <Box
              component="span"
              sx={{
                fontWeight: 900,
                background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.text.primary})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                position: "relative",
                display: "inline-block",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  bottom: "-4px",
                  left: 0,
                  width: "100%",
                  height: "2px",
                  background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, transparent 100%)`,
                }
              }}
            >
              Questions
            </Box>
          </Typography>

          <Typography
            sx={{
              color: theme.palette.text.secondary,
              fontSize: { xs: 14, sm: 16 },
              lineHeight: 1.8,
              maxWidth: 570,
              mx: "auto",
            }}
          >
            Everything you need to know about ArthGyan, our financial
            services, and how to get started.
          </Typography>

          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={1}
            sx={{ mt: 2.5 }}
          >
          </Stack>

          {/* Main content */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "0.78fr 1.22fr" },
              gap: { xs: 4, md: 6 },
              alignItems: "start",
            }}
          >
            {/* Left panel */}
            <Box
              sx={{
                position: { md: "sticky" },
                top: 100,
              }}
            >
              <Box
                sx={{
                  p: { xs: 2, sm: 3 },
                  borderRadius: "24px",
                  backgroundColor: "#FFFFFF",
                  border: "1px solid #E7EDF6",
                  boxShadow: "0 12px 38px rgba(24, 63, 120, 0.06)",
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    borderRadius: "18px",
                    overflow: "hidden",
                    background: `linear-gradient(145deg, ${primary}09, #EAF7FF)`,
                    mb: 3,
                  }}
                >
                  <Box
                    component="img"
                    src="https://ik.imagekit.io/arthgyan/Arthgyan_Web/faq"
                    alt="ArthGyan frequently asked questions"
                    loading="lazy"
                    sx={{
                      display: "block",
                      width: "100%",
                      maxWidth: 430,
                      mx: "auto",
                      height: { xs: 230, sm: 290, md: 310 },
                      objectFit: "contain",
                      p: 1,
                    }}
                  />
                </Box>

                <Typography
                  sx={{
                    fontSize: 22,
                    fontWeight: 800,
                    color: theme.palette.text.primary,
                    mb: 1,
                  }}
                >
                  Need clarity?
                </Typography>

                <Typography
                  sx={{
                    fontSize: 14,
                    lineHeight: 1.8,
                    color: theme.palette.text.secondary,
                    mb: 2.5,
                  }}
                >
                  Explore answers about investments, insurance, loans, and
                  using ArthGyan with confidence.
                </Typography>

                <Divider sx={{ borderColor: "#E8EEF7", mb: 2 }} />

                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box
                    sx={{
                      width: 42,
                      height: 42,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "13px",
                      color: primary,
                      backgroundColor: `${primary}10`,
                      flexShrink: 0,
                    }}
                  >
                    <HelpOutlineRoundedIcon />
                  </Box>

                  <Box>
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: theme.palette.text.primary,
                      }}
                    >
                      Simple answers, smarter choices
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: theme.palette.text.secondary,
                        mt: 0.3,
                      }}
                    >
                      Your financial journey starts here.
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Box>

            {/* FAQ list */}
            <Box>
              {faqSections.map((section, sectionIndex) => (
                <Box key={section.title} sx={{ mb: 3.5 }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                    sx={{ mb: 1.5 }}
                  >
                    <Box
                      sx={{
                        width: 38,
                        height: 38,
                        borderRadius: "12px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        color: primary,
                        fontSize: 13,
                        fontWeight: 800,
                        background: `linear-gradient(135deg, ${primary}18, ${primary}08)`,
                        border: `1px solid ${primary}15`,
                      }}
                    >
                      {String(sectionIndex + 1).padStart(2, "0")}
                    </Box>

                    <Box sx={{ flexGrow: 1 }}>
                      <Typography
                        component="h2"
                        sx={{
                          fontSize: { xs: 16, md: 18 },
                          fontWeight: 800,
                          color: theme.palette.text.primary,
                        }}
                      >
                        {section.title}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 12,
                          color: theme.palette.text.secondary,
                          mt: 0.2,
                        }}
                      >
                        {section.questions.length}{" "}
                        {section.questions.length === 1
                          ? "question"
                          : "questions"}
                      </Typography>
                    </Box>
                  </Stack>

                  <Stack spacing={1.2}>
                    {section.questions.map((faq, questionIndex) => {
                      const id = `${sectionIndex}-${questionIndex}`;
                      const isExpanded = expandedIndex === id;

                      return (
                        <Accordion
                          key={id}
                          expanded={isExpanded}
                          onChange={handleAccordionChange(id)}
                          disableGutters
                          elevation={0}
                          sx={{
                            border: `1px solid ${isExpanded ? `${primary}65` : "#E7EDF5"
                              }`,
                            borderRadius: "14px !important",
                            overflow: "hidden",
                            backgroundColor: "#FFFFFF",
                            transition:
                              "border-color 0.25s ease, box-shadow 0.25s ease",
                            boxShadow: isExpanded
                              ? `0 8px 24px ${primary}12`
                              : "0 2px 7px rgba(15, 35, 70, 0.025)",
                            "&:before": { display: "none" },
                            "&:hover": {
                              borderColor: `${primary}60`,
                            },
                          }}
                        >
                          <AccordionSummary
                            expandIcon={
                              <Box
                                sx={{
                                  width: 30,
                                  height: 30,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  borderRadius: "10px",
                                  backgroundColor: isExpanded
                                    ? primary
                                    : `${primary}0C`,
                                  color: isExpanded ? "#FFFFFF" : primary,
                                  transition: "all 0.25s ease",
                                }}
                              >
                                <ExpandMoreRoundedIcon
                                  sx={{
                                    fontSize: 21,
                                    transform: isExpanded
                                      ? "rotate(180deg)"
                                      : "rotate(0deg)",
                                    transition: "transform 0.25s ease",
                                  }}
                                />
                              </Box>
                            }
                            sx={{
                              px: { xs: 1.8, sm: 2.3 },
                              py: 0.7,
                              minHeight: 64,
                              "&.Mui-expanded": { minHeight: 64 },
                              "& .MuiAccordionSummary-content": {
                                my: 1.5,
                                mr: 1.5,
                              },
                              "& .MuiAccordionSummary-content.Mui-expanded": {
                                my: 1.5,
                              },
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: { xs: 13.5, sm: 14.5, md: 15 },
                                fontWeight: isExpanded ? 700 : 600,
                                lineHeight: 1.65,
                                color: isExpanded
                                  ? primary
                                  : theme.palette.text.primary,
                                transition: "color 0.2s ease",
                              }}
                            >
                              {faq.question}
                            </Typography>
                          </AccordionSummary>

                          <AccordionDetails
                            sx={{
                              px: { xs: 2, sm: 2.5 },
                              pt: 0,
                              pb: 2.2,
                            }}
                          >
                            <Divider
                              sx={{
                                mb: 1.8,
                                borderColor: `${primary}18`,
                              }}
                            />

                            <Typography
                              sx={{
                                fontSize: { xs: 13, sm: 14 },
                                color: theme.palette.text.secondary,
                                lineHeight: 1.9,
                              }}
                            >
                              {faq.answer}
                            </Typography>
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                  </Stack>
                </Box>
              ))}
            </Box>
          </Box>
        </Container>
      </Box>
    </>

  );
};

export default FAQPage;
